<template>
  <v-expansion-panel id="processing-results">
    <v-expansion-panel-header ripple id="processing-results-header">
      <v-row>
        <v-col cols="8">
          <span class="body-1 grey--text text--darken-1 output-header-txt"
            v-text="!selected ? 'Select the outputs' : 'Outputs:'" />
        </v-col>
        <v-col cols="3" class="text--secondary px-0">
          <v-row align="center" class="fill-height ma-0">
            <span style="margin-left: -5px;" class="accent--text">{{ selected | displaySelected }} </span>
          </v-row>
        </v-col>
      </v-row>
    </v-expansion-panel-header>
    <v-expansion-panel-content id="processing-results-content">
      <v-container fluid class="pa-0">
        <v-list rounded>
          <v-list-item-group variant="text" id="processing-results-list" v-model="selected" multiple>
            <processing-result-item-shared v-for="(item, idx) in results" :key="idx" :processing="Processing"
              :selected="selected" :histogram-graph="item['json_lst_hist_graph']" :tile-name="item['ab_tile_name']"
              :total-bands="item['int_total_bands']" :raster-name="item['ab_raster_name']"
              :stats-min="item['fl_stats_min']" :stats-max="item['fl_stats_max']" :min="item['fl_min_val']"
              :max="item['fl_max_val']" show-histogram />
            <v-list-item id="level-curve" class="text-truncate" value="level_curve" :disabled="isLevelCurveDisabled"
              v-if="isProcessingDateAfter">
              <v-list-item-icon>
                <v-icon>mdi-image</v-icon>
              </v-list-item-icon>
              <v-tooltip bottom>
                <template v-slot:activator="{ on }">
                  <v-checkbox @change="showLevelCurve" :disabled="isLevelCurveDisabled" class="aero-output-checkbox"
                    color="#00B3FF" :value="selected.includes('level_curve')" hide-details></v-checkbox>
                  <v-list-item-content v-on="on" @click="showLevelCurve">
                    {{ 'Contours' }}
                  </v-list-item-content>
                </template>
                <span>{{ 'Contours' }}</span>
              </v-tooltip>
            </v-list-item>
          </v-list-item-group>
        </v-list>
      </v-container>
    </v-expansion-panel-content>
  </v-expansion-panel>
</template>

<script>
import { isEmpty, isEqual, eq } from "lodash/lang"
import { head, intersection } from 'lodash/array'
import { MapViewEventBus, ProcessingsTilesEventBus } from "../../../../helpers/mapview/events"
import ProcessingResultItemShared from "../../Common/ProcessingResultItemShared"
import { TILE_TYPE_NAMES } from "../../../../helpers/mapview/constants"
import { FEATURE_GROUPS, PANES } from "../../../../helpers/leaflet/constants"
import { listHistogramConfigsShared } from "../../../../api/api-tile"
import { getLevelCurveShared } from "../../../../api/api-project"
import { sortBy } from "lodash/collection"
// import { ConpassEventBus } from '../../../../helpers/conpass/events'

import Loading from "vue-loading-overlay";
import "vue-loading-overlay/dist/vue-loading.css";

import Vue from "vue";
Vue.use(Loading);

const _Map = Symbol.for("_Map")
const _Processing = Symbol.for("_Processing")

export default {
  name: "ProcessingResultsPanelShared",
  components: { ProcessingResultItemShared },
  inject: { _Map, _Processing },
  data: () => ({
    selected: [],
    results: [],
    level_curve: 'level_curve',
    loader: null,
  }),
  methods: {
    showLevelCurve() {
      if (!this.selected.includes('level_curve')) {
        this.addLevelCurve()
      } else {
        this.Map.featureGroups[FEATURE_GROUPS.POST_PROCESSINGS_RESULTS].clearLayers();
      }
    },
    addLevelCurve() {
      this.loader = this.$loading.show({
        container: null,
        canCancel: false,
        backgroundColor: "#000",
        color: "#fff",
        opacity: 0.5,
        loader: "spinner",
        onCancel: this.onCancel,
      });

      getLevelCurveShared(this.Processing["id_processing"])
        .then(res => {
          const presigned_url = res.data.data.presigned_url
          fetch(presigned_url)
            .then(response => response.json())
            .then(results => {
              console.log(results);
              if (!isEmpty(results)) {
                window.L.geoJSON(
                  results,
                  { pane: PANES.POST_PROCESSINGS_RESULTS, },
                  this.Map
                ).addTo(this.Map.featureGroups[FEATURE_GROUPS.POST_PROCESSINGS_RESULTS]);
              }
              this.loader.hide();
            }
            )
        })
        .catch(() => {
          this.loader.hide();
        })
    },
    onChange(args) {
      const { processing, tileNames } = args
      if (isEqual(this.Processing["id_processing"], processing["id_processing"])) {
        this.selected = tileNames
      }
    },
    async onSetupComplete() {
      await listHistogramConfigsShared(this.Processing["id_processing"])
        .then(res => {
          const results = res.data.data
          if (!isEmpty(results)) {

            // Ordering the histogram graph
            for (let data of results) {
              if (!data["json_lst_hist_graph"]) continue
              data["json_lst_hist_graph"] = sortBy(data["json_lst_hist_graph"], (a) => {
                return a["pixel_value"]
              })
            }

            this.results = results
            const firstResult = head(results)
            this.selected = [firstResult["ab_tile_name"]]
          }
        })
    },
    onStartFlow() {
      const firstResult = head(this.results)
      const intersectedArray = intersection(this.selected, [firstResult['ab_tile_name']])
      if (intersectedArray.length === this.selected.length) return

      this.selected = []
      ProcessingsTilesEventBus.$emit('clear')
      this.$nextTick()
        .then(() => {
          this.selected = [firstResult['ab_tile_name']]
        })
    }
  },
  filters: {
    displaySelected(value) {
      let displayValue
      if (isEmpty(value))
        displayValue = ""
      else if (eq(value.length, 1))
        displayValue = TILE_TYPE_NAMES[head(value)]
      else
        displayValue = `${TILE_TYPE_NAMES[head(value)]} (+${value.length - 1})`
      if (!displayValue) return ""
      return displayValue
    },
  },
  computed: {
    Map() {
      return this._Map()
    },
    Processing() {
      return this._Processing()
    },
    isLevelCurveDisabled() {
      if (!this.Map) return true
      return this.selected.length > 1 && !this.selected.includes('level_curve')
    },
    isProcessingDateAfter() {
      const createdDate = new Date(this.Processing["dh_created"]).toISOString().split('T')[0];
      return createdDate > "2023-08-16"
    },
  },
  created() {
    MapViewEventBus.$on("setup-complete", this.onSetupComplete)
    ProcessingsTilesEventBus.$on("change", this.onChange)
    // ConpassEventBus.$on("startflow", this.onStartFlow)
  },
  watch: {
    selected(newValue, oldValue) {
      if (!newValue.includes('level_curve')) {
        ProcessingsTilesEventBus.$emit("change", {
          processing: this.Processing,
          tileNames: newValue
        })
        if (newValue.length > oldValue.length) {
          const diffArr = newValue.filter(x => !oldValue.includes(x))
          ProcessingsTilesEventBus.$emit("add", {
            processing: this.Processing,
            tileName: diffArr[0]
          })
        } else {
          const diffArr = oldValue.filter(x => !newValue.includes(x))
          ProcessingsTilesEventBus.$emit("remove", {
            processing: this.Processing,
            tileName: diffArr[0]
          })
        }
      }
    }

  }
}
</script>

<style scoped lang="scss">
.active {
  background: #333333;
  color: #FFFFFF;
}

::v-deep .v-btn__content {
  width: 100%;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: normal;
}
</style>
